import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";

const treatments = [
  {
    title: "離子紅光逆齡抗衰老",
    benefit: "撫平皺紋、緊緻輪廓，重現年輕飽滿肌膚",
    points: ["眉稜提升", "去除川字紋、法令紋、木偶紋", "緊緻下顎線與側面輪廓", "消除雙下巴、老人凹"],
    img: "https://storage.helloreaddy.io/project_files/5083fe3c-0446-4f05-ab30-834aed56ded7/c2927ca9-cb40-4ac4-ac07-fe139813e348_compressed_-58.webp",
    imageAspect: "aspect-[2/1]",
    imageFit: "object-cover object-center",
    framed: true,
  },
  {
    title: "離子紅光去妊娠紋",
    benefit: "修復妊娠紋與肥胖紋，回復平滑緊緻肌膚",
    points: ["輕度、中度、重度妊娠紋修復", "去除肥胖紋", "改善產後鬆弛"],
    img: "https://storage.helloreaddy.io/project_files/5083fe3c-0446-4f05-ab30-834aed56ded7/1bc173f4-d566-4c54-afd0-9d573a6d9f57_compressed_-59.webp",
    imageAspect: "aspect-[2/1]",
    imageFit: "object-cover object-center",
    framed: true,
  },
  {
    title: "高階全科產後修復",
    benefit: "度身訂造專屬產後修復方案",
    points: ["紮肚技術", "盆骨閉合評估", "養宮、催乳調理"],
    img: "https://storage.helloreaddy.io/project_files/5083fe3c-0446-4f05-ab30-834aed56ded7/08fe4b49-45cf-4eb9-8a17-79a4e97d1724_compressed_-60.webp",
    imageAspect: "aspect-[2/1]",
    imageFit: "object-cover object-center",
    framed: true,
  },
];

export default function Treatments() {
  const trackRef = useRef<HTMLDivElement>(null);
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(true);

  const updateArrows = () => {
    const el = trackRef.current;
    if (!el) return;
    setCanPrev(el.scrollLeft > 4);
    setCanNext(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  };

  useEffect(() => {
    const el = trackRef.current;
    if (!el) return;
    updateArrows();
    el.addEventListener("scroll", updateArrows, { passive: true });
    window.addEventListener("resize", updateArrows);
    return () => {
      el.removeEventListener("scroll", updateArrows);
      window.removeEventListener("resize", updateArrows);
    };
  }, []);

  const scrollByCard = (dir: number) => {
    const el = trackRef.current;
    if (!el) return;
    const card = el.querySelector("article") as HTMLElement | null;
    const amount = card ? card.offsetWidth + 24 : el.clientWidth * 0.8;
    el.scrollBy({ left: dir * amount, behavior: "smooth" });
  };

  return (
    <section id="treatments" className="py-20 md:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <div className="inline-block px-5 py-1.5 rounded-full bg-brand-600 text-white text-sm tracking-wider">治療服務</div>
          <h2 className="mt-6 font-serif text-3xl md:text-5xl text-brand-800">
            專業療程 · 為你重現健康美肌
          </h2>
          <p className="mt-5 text-brand-700/70 max-w-2xl mx-auto">
            以離子紅光細胞再生技術為核心，針對不同肌膚問題提供安全、溫和的專業修復方案，適合孕婦及幼兒。
          </p>
        </div>

        <div className="relative">
          <div
            ref={trackRef}
            className="flex gap-6 overflow-x-auto snap-x snap-mandatory scroll-smooth no-scrollbar pb-2"
          >
            {treatments.map((t) => (
              <article
                key={t.title}
                className="group bg-[#f4f8f5] rounded-lg overflow-hidden border border-brand-100 hover:border-brand-300 transition flex flex-col w-full sm:w-[74%] lg:w-[46%] flex-none snap-start"
              >
                <div
                  className={`relative w-full ${t.imageAspect} overflow-hidden bg-[#eef3ef]`}
                >
                  {t.framed ? (
                    <div className="absolute inset-3 md:inset-4 rounded-lg overflow-hidden border border-brand-200/70">
                      <img
                        src={t.img}
                        alt={t.title}
                        className={`w-full h-full ${t.imageFit} group-hover:scale-105 transition-transform duration-500`}
                      />
                      <div className="absolute inset-y-0 left-1/2 -translate-x-1/2 w-px bg-white/80"></div>
                      <span className="absolute bottom-3 left-3 px-3 py-1 rounded-full bg-black/50 text-white text-xs tracking-widest backdrop-blur-sm">療程前</span>
                      <span className="absolute bottom-3 right-3 px-3 py-1 rounded-full bg-black/50 text-white text-xs tracking-widest backdrop-blur-sm">療程後</span>
                    </div>
                  ) : (
                    <img
                      src={t.img}
                      alt={t.title}
                      className={`w-full h-full ${t.imageFit} group-hover:scale-105 transition-transform duration-500`}
                    />
                  )}
                </div>
                <div className="p-6 md:p-7 flex-1 flex flex-col">
                  <h3 className="text-lg md:text-xl font-semibold text-brand-800">{t.title}</h3>
                  <p className="mt-2 text-sm text-brand-700 font-medium">{t.benefit}</p>
                  <ul className="mt-4 space-y-1.5">
                    {t.points.map((p, i) => (
                      <li key={i} className="flex gap-2 text-sm text-brand-900/75 leading-relaxed">
                        <i className="ri-checkbox-circle-line text-brand-600 mt-0.5"></i>
                        <span>{p}</span>
                      </li>
                    ))}
                  </ul>
                  <div className="mt-6 pt-5 border-t border-brand-100 flex items-center gap-3">
                    <a
                      href="tel:+85269438757"
                      className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-brand-600 hover:bg-brand-700 text-white text-sm whitespace-nowrap cursor-pointer transition"
                    >
                      <i className="ri-phone-line"></i> 預約查詢
                    </a>
                    <a
                      href="https://wa.me/85269438757"
                      target="_blank"
                      rel="nofollow noreferrer"
                      className="w-10 h-10 flex items-center justify-center rounded-md border border-brand-300 text-brand-700 hover:bg-brand-50 cursor-pointer transition"
                      aria-label="WhatsApp 查詢"
                    >
                      <i className="ri-whatsapp-line text-lg"></i>
                    </a>
                  </div>
                </div>
              </article>
            ))}
          </div>

          <button
            type="button"
            onClick={() => scrollByCard(-1)}
            aria-label="上一個療程"
            className={`absolute left-2 md:left-3 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/95 border border-brand-200 text-brand-700 flex items-center justify-center transition cursor-pointer ${canPrev ? "opacity-100 hover:bg-brand-50" : "opacity-0 pointer-events-none"}`}
          >
            <i className="ri-arrow-left-s-line text-xl"></i>
          </button>
          <button
            type="button"
            onClick={() => scrollByCard(1)}
            aria-label="下一個療程"
            className={`absolute right-2 md:right-3 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/95 border border-brand-200 text-brand-700 flex items-center justify-center transition cursor-pointer ${canNext ? "opacity-100 hover:bg-brand-50" : "opacity-0 pointer-events-none"}`}
          >
            <i className="ri-arrow-right-s-line text-xl"></i>
          </button>
        </div>

        <div className="mt-12 text-center">
          <Link
            to="/treatments"
            className="inline-flex items-center gap-2 px-8 py-3.5 rounded-md border border-brand-300 text-brand-700 hover:bg-brand-50 text-sm tracking-wider whitespace-nowrap cursor-pointer transition"
          >
            查看全部療程資訊 <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
      </div>
    </section>
  );
}